import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { teamAPI } from '../services/api';
import { adminAPI } from '../services/adminAPI';
import Footer from '../components/Footer';
import Navbar from '../components/Navbar';
import './CreateTeam.css';
import { useBackgroundRemoval } from '../hooks/useBackgroundRemoval';

function CreateTeam() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [teamLimitInfo, setTeamLimitInfo] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [seasons, setSeasons] = useState([]);
  const [ageGroups, setAgeGroups] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    sport: '',
    primaryColor: '#003366',
    secondaryColor: '#FFFFFF',
    season: '',
    ageGroup: '',
  });

  // Logo state
  // eslint-disable-next-line
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [originalLogoImage, setOriginalLogoImage] = useState(null);
  const [cutoutLogoImage, setCutoutLogoImage] = useState(null);
  const [activeLogoVersion, setActiveLogoVersion] = useState('original');

  const { removeBg: removeLogoBg, isRemoving: isRemovingLogo, bgRemovalError: logoRemovalError } = useBackgroundRemoval();

  const [toastMessage, setToastMessage] = useState({ type: '', text: '' });
  // Helper function to show toast messages
  const showToast = (type, text, duration = 3000) => {
    setToastMessage({ type, text });
    setTimeout(() => setToastMessage({ type: '', text: '' }), duration);
  };

  // Fetch subscription info on mount
  useEffect(() => {
    const fetchLimitInfo = async () => {
      try {
        const response = await teamAPI.getLimitInfo();
        setTeamLimitInfo(response.data);
      } catch (err) {
        console.error('Error fetching limit info:', err);
      }
    };
    fetchLimitInfo();
  }, []);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    fetchActiveSeasons();
    fetchActiveAgeGroups();
  }, []);

  const fetchActiveSeasons = async () => {
    try {
      const response = await adminAPI.getActiveSeasons();
      setSeasons(response.data);
    } catch (error) {
      console.error('Error fetching seasons:', error);
    }
  };

  const fetchActiveAgeGroups = async () => {
    try {
      const response = await adminAPI.getActiveAgeGroups();
      setAgeGroups(response.data);
    } catch (error) {
      console.error('Error fetching age groups:', error);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // Handle logo file selection
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        setError('Logo file must be less than 5MB');
        return;
      }

      // Validate file type
      const allowedTypes = [
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/gif',
        'image/svg+xml',
      ];
      if (!allowedTypes.includes(file.type)) {
        setError('Logo must be an image file (JPEG, PNG, GIF, or SVG)');
        return;
      }

      setLogoFile(file);
      setError('');

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
        setOriginalLogoImage(reader.result);
        setCutoutLogoImage(null);
        setActiveLogoVersion('original');
      };
      reader.readAsDataURL(file);
    }
  };

  // Remove logo
  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
  };

  const handleRemoveLogoBg = async () => {
    if (!originalLogoImage) return;
    const result = await removeLogoBg(originalLogoImage);
    if (result) {
      const { blobUrl, toBase64 } = result;
      setCutoutLogoImage(blobUrl);
      setLogoPreview(blobUrl);
      setActiveLogoVersion('cutout');
      toBase64.then((base64) => {
        setCutoutLogoImage(base64);
        setLogoPreview(base64);
      });
    }
  };

  const handleToggleLogoVersion = (version) => {
    setActiveLogoVersion(version);
    setLogoPreview(version === 'cutout' ? cutoutLogoImage : originalLogoImage);
  };

  // Clear form
  const clearForm = () => {
    setFormData({
      name: '',
      primaryColor: '#003366',
      secondaryColor: '#FFFFFF',
      season: '',
      ageGroup: '',
    });
    setLogoFile(null);
    setLogoPreview(null);
    setError('');
    window.scrollTo(0, 0);
  };

  // Save team function (reusable)
  const saveTeam = async () => {
    setError('');
    setSuccessMessage('');
    setLoading(true);

    try {
      // Create JSON payload instead of FormData
      const submitData = {
        name: formData.name,
        sport: 'Baseball',
        primaryColor: formData.primaryColor,
        secondaryColor: formData.secondaryColor,
        season: formData.season,
        ageGroup: formData.ageGroup,
        logoUrl: logoPreview || null, // Send base64 string directly
      };

      await teamAPI.create(submitData);
      return true;
    } catch (err) {
      // Handle team limit error
      if (
        err.response?.status === 403 &&
        err.response?.data?.error === 'Team limit reached'
      ) {
        showToast(
          'error',
          `${err.response.data.message}. Redirecting to upgrade options...`,
          3000
        );
        setTimeout(() => navigate('/membership'), 2000);
      }
      // Handle logo upload restriction error
      else if (
        err.response?.status === 403 &&
        err.response?.data?.error === 'Logo upload restricted'
      ) {
        showToast(
          'error',
          `${err.response.data.message}. Redirecting to upgrade options...`,
          3000
        );
        setTimeout(() => navigate('/membership'), 2000);
      } else {
        setError(err.response?.data?.error || 'Failed to create team');
      }
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Save and go to dashboard
  const handleSubmit = async (e) => {
    e.preventDefault();
    const success = await saveTeam();
    if (success) {
      navigate(`/team/${formData.selectedTeamId}`);
    }
  };

  // Save and create another
  const handleSaveAndCreateAnother = async (e) => {
    e.preventDefault();

    const success = await saveTeam();
    if (success) {
      // Re-fetch limit info FIRST to get updated canCreateMore status
      try {
        const response = await teamAPI.getLimitInfo();
        const updatedLimitInfo = response.data;
        setTeamLimitInfo(updatedLimitInfo);

        // NOW check if user can create another team
        if (!updatedLimitInfo.canCreateMore) {
          showToast(
            'error',
            `You've reached your team limit (${updatedLimitInfo.teamLimit} teams) on the ${updatedLimitInfo.subscriptionPlan} plan. Redirecting...`,
            3000
          );
          setTimeout(() => navigate('/membership'), 2000);
          return;
        }

        // Only clear form if they CAN create more
        setSuccessMessage('✅ Team created! Create another below.');
        clearForm();
        setTimeout(() => setSuccessMessage(''), 3000);
      } catch (err) {
        console.error('Error fetching updated limit info:', err);
      }
    }
  };

  // Logo upload is available to all tiers

  if (teamLimitInfo && !teamLimitInfo.canCreateMore) {
    return (
      <div className="create-team-container">
        <div className="empty-state-graphic">
          <h2>Team Limit Reached</h2>
          <p>
            You've reached your limit of {teamLimitInfo.teamLimit} team
            {teamLimitInfo.teamLimit === 1 ? '' : 's'} on the{' '}
            {teamLimitInfo.subscriptionPlan} plan.
          </p>
          <p>Upgrade to create more teams or delete an existing one.</p>
          <div
            style={{
              display: 'flex',
              gap: '16px',
              justifyContent: 'center',
              marginTop: '24px',
            }}
          >
            <button
              className="btn btn-primary"
              onClick={() => navigate('/membership')}
            >
              ⭐ View Upgrade Options
            </button>
            <button
              className="btn btn-primary"
              onClick={() => navigate(`/team/${formData.selectedTeamId}`)}
            >
              Back to Team Portal
            </button>
          </div>
        </div>
      </div>
    );
  }
  return (
    <>
      <Navbar />
      <div className="create-team-container">
        <div className="create-team-content">
          <div className="form-header">
            <button
              className="btn btn-primary"
              style={{ width: 'auto', flexShrink: 0, marginBottom: 10 }}
              onClick={() => navigate(-1)}
            >
              ← Back
            </button>

            <h1>Create Your Team</h1>
            <p>Set up your team profile and colors</p>
          </div>

          {error && <div className="error-message">{error}</div>}

          {successMessage && (
            <div className="success-message">{successMessage}</div>
          )}

          <form onSubmit={handleSubmit} className="team-form">
            {/* Logo Upload Section - Only for Pro/Premium */}
            <div className="form-group logo-upload-section">
              <label>Team Logo</label>
              {teamLimitInfo && teamLimitInfo.subscriptionPlan === 'free' ? (
                <div className="upgrade-prompt" style={{
                  background: 'linear-gradient(135deg, #6b0000, #1a0000)',
                  borderRadius: '12px',
                  padding: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  cursor: 'pointer',
                }} onClick={() => navigate('/membership')}>
                  <span style={{ fontSize: '32px' }}>⭐</span>
                  <div>
                    <div style={{ color: '#fff', fontWeight: 700, fontSize: '16px' }}>Upgrade to Add Team Logos</div>
                    <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px' }}>Logo uploads are available on Pro and Premium plans</div>
                  </div>
                </div>
              ) : (
              <div className="logo-upload-container">
                {logoPreview ? (
                  <div className="logo-preview">
                    <img src={logoPreview} alt="Team logo preview" />
                    {isRemovingLogo && (
                      <div style={{
                        position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', borderRadius: '8px',
                      }}>
                        <div style={{
                          width: '28px', height: '28px',
                          border: '3px solid rgba(255,255,255,0.3)',
                          borderTopColor: '#fff', borderRadius: '50%',
                          animation: 'bg-removal-spin 0.75s linear infinite',
                        }} />
                        <p style={{ color: '#fff', fontSize: '12px', margin: '8px 0 0' }}>Removing…</p>
                      </div>
                    )}
                    <button
                      type="button"
                      className="btn-remove-logo"
                      onClick={handleRemoveLogo}
                      disabled={loading || isRemovingLogo}
                    >
                      ✕ Remove Logo
                    </button>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={handleRemoveLogoBg}
                      disabled={isRemovingLogo || loading}
                      style={{ marginTop: '8px', width: '100%' }}
                    >
                      {isRemovingLogo ? 'Removing background…' : '✂️ Remove Background'}
                    </button>
                    {logoRemovalError && (
                      <p style={{ color: '#e53e3e', fontSize: '12px', marginTop: '4px' }}>{logoRemovalError}</p>
                    )}
                    {originalLogoImage && cutoutLogoImage && (
                      <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                        <button
                          type="button"
                          className={activeLogoVersion === 'original' ? 'btn-primary' : 'btn-secondary'}
                          onClick={() => handleToggleLogoVersion('original')}
                          style={{ flex: 1 }}
                          disabled={isRemovingLogo}
                        >
                          Original
                        </button>
                        <button
                          type="button"
                          className={activeLogoVersion === 'cutout' ? 'btn-primary' : 'btn-secondary'}
                          onClick={() => handleToggleLogoVersion('cutout')}
                          style={{ flex: 1 }}
                          disabled={isRemovingLogo}
                        >
                          ✂️ Cutout
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <label
                    htmlFor="logo-upload"
                    className="logo-upload-placeholder"
                  >
                    <span className="upload-icon">📸</span>
                    <p>Upload Team Logo</p>
                    <p className="upload-hint">PNG, JPG, GIF or SVG (Max 5MB)</p>
                  </label>
                )}
                <input
                  id="logo-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleLogoChange}
                  className="logo-file-input"
                  disabled={loading}
                />
              </div>
              )}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name">Team Name *</label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your team"
                  required
                  disabled={loading}
                  maxLength={25}
                />
                <div className="char-counter">
                  {formData.name.length}/25 characters
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="sport">Sport *</label>
                <select
                  id="sport"
                  name="sport"
                  value={formData.sport}
                  onChange={handleChange}
                  required
                  disabled={loading}
                >
                  <option value="">Select sport</option>
                  <option value="Baseball">Baseball</option>
                  <option value="Softball">Softball</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="ageGroup">Age Group *</label>
                <select
                  id="ageGroup"
                  name="ageGroup"
                  value={formData.ageGroup}
                  onChange={handleChange}
                  required
                  disabled={loading}
                >
                  <option value="">Select age group</option>
                  {ageGroups.map((ageGroup) => (
                    <option key={ageGroup.id} value={ageGroup.name}>
                      {ageGroup.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="season">Season *</label>
                <select
                  id="season"
                  name="season"
                  value={formData.season}
                  onChange={handleChange}
                  required
                  disabled={loading}
                >
                  <option value="">Select season</option>
                  {seasons.map((season) => (
                    <option key={season.id} value={season.name}>
                      {season.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="primaryColor">Primary Color</label>
                <div className="color-input-wrapper">
                  <input
                    id="primaryColor"
                    name="primaryColor"
                    type="color"
                    value={formData.primaryColor}
                    onChange={handleChange}
                    disabled={loading}
                  />
                  <input
                    type="text"
                    value={formData.primaryColor}
                    onChange={(e) =>
                      setFormData({ ...formData, primaryColor: e.target.value })
                    }
                    placeholder="#003366"
                    className="color-text-input"
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="secondaryColor">Secondary Color</label>
                <div className="color-input-wrapper">
                  <input
                    id="secondaryColor"
                    name="secondaryColor"
                    type="color"
                    value={formData.secondaryColor}
                    onChange={handleChange}
                    disabled={loading}
                  />
                  <input
                    type="text"
                    value={formData.secondaryColor}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        secondaryColor: e.target.value,
                      })
                    }
                    placeholder="#FFFFFF"
                    className="color-text-input"
                    disabled={loading}
                  />
                </div>
              </div>
            </div>

            <div className="color-preview">
              <div className="preview-label">Color Preview:</div>
              <div
                className="preview-swatch"
                style={{
                  backgroundColor: formData.primaryColor,
                  border: `4px solid ${formData.secondaryColor}`,
                }}
              >
                <span style={{ color: formData.secondaryColor }}>
                  {formData.name || 'Team Name'}
                </span>
              </div>
            </div>

            <div className="form-actions">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? 'Creating Team...' : 'Create Team'}
              </button>
              {teamLimitInfo &&
              (teamLimitInfo.subscriptionPlan === 'free' ||
                (teamLimitInfo.teamLimit &&
                  teamLimitInfo.currentTeams >=
                    teamLimitInfo.teamLimit - 1)) ? (
                <button
                  type="button"
                  className="btn-upgrade-teams"
                  onClick={() => navigate('/membership')}
                  disabled={loading}
                >
                  ⭐ Upgrade for More Teams
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSaveAndCreateAnother}
                  disabled={loading}
                >
                  {loading ? 'Saving...' : 'Save & Create Another'}
                </button>
              )}
            </div>
          </form>
        </div>
        {/* Toast Notifications */}
        {toastMessage.text && toastMessage.type === 'success' && (
          <div className="toast-message toast-success">{toastMessage.text}</div>
        )}
        {toastMessage.text && toastMessage.type === 'error' && (
          <div className="toast-message toast-error">{toastMessage.text}</div>
        )}
      </div>
      <Footer />
    </>
  );
}

export default CreateTeam;
